import useGuardedRealtimeList from '../../realtime/useGuardedRealtimeList';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Form, Modal, Spinner, Table } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { portalApi } from '../../redux/api/portalApi';
import { selectApiUrls } from '../../redux/slices/apiUrlSlice';
import { selectDropdownOptions } from '../../redux/slices/dropdownSlice';
import { normalizeText } from './assemblyScheduleConfig';
import './assembly.css';

const ASSEMBLER_DROPDOWN_TYPE = 'Assembler';
const EMPTY_FORM = Object.freeze({
  firstName: '',
  lastName: '',
  isActive: true,
});

const normalizeDisplayText = (value) => normalizeText(value).replace(/\s+/g, ' ');
const buildDisplayText = (values) => normalizeDisplayText([
  values.firstName,
  values.lastName,
].filter(Boolean).join(' '));

const splitDisplayText = (value) => {
  const parts = normalizeDisplayText(value).split(' ').filter(Boolean);
  return {
    firstName: parts[0] || '',
    lastName: parts.slice(1).join(' '),
  };
};

const generateValueFromDisplayText = (text) => (
  normalizeDisplayText(text).toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '')
);

const getOptionId = (option) => option?.ID ?? option?.id;

const getOptionBoolean = (value) => ![false, 0, '0', 'false'].includes(
  typeof value === 'string' ? value.toLowerCase().trim() : value
);

const getAssemblerOptionLabel = (option) => normalizeDisplayText(
  option?.DisplayText ?? option?.displayText ?? option?.Value ?? option?.value
);

const getAssemblerOptionSortOrder = (option) => {
  const sortOrder = Number(option?.SortOrder ?? option?.sortOrder);
  return Number.isFinite(sortOrder) ? sortOrder : Number.MAX_SAFE_INTEGER;
};

const isAssemblerDropdownOption = (option) => (
  normalizeDisplayText(option?.DropdownType ?? option?.dropdownType).toLowerCase() ===
  ASSEMBLER_DROPDOWN_TYPE.toLowerCase()
);

const AssemblyAssemblerManager = ({ canEdit = false }) => {
  const dispatch = useDispatch();
  const apiUrls = useSelector(selectApiUrls);
  const cachedDropdownOptions = useSelector(selectDropdownOptions);
  const dropdownValuesUrl = apiUrls?.dropdownValues || '/dropdown-values';

  const [dropdownRows, setDropdownRows] = useState(null);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState(EMPTY_FORM);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [notice, setNotice] = useState(null);

  useGuardedRealtimeList({ apiKey: 'dropdownValues', apply: setDropdownRows,
    blocked: isLoading || isSaving || editingId !== null || Boolean(deleteTarget) || Boolean(formData.firstName || formData.lastName),
  });

  const assemblerOptions = useMemo(() => {
    const rows = dropdownRows ?? cachedDropdownOptions;
    return (Array.isArray(rows) ? rows : [])
      .filter(isAssemblerDropdownOption)
      .sort((left, right) => (
        getAssemblerOptionLabel(left).localeCompare(getAssemblerOptionLabel(right), undefined, {
          sensitivity: 'base',
        })
      ));
  }, [cachedDropdownOptions, dropdownRows]);

  const refreshDropdownValues = useCallback(async () => {
    setIsLoading(true);
    setNotice(null);
    let rows = [];
    try {
      const response = await dispatch(
        portalApi.endpoints.getDropdownValues.initiate(dropdownValuesUrl, {
          forceRefetch: true,
          subscribe: false,
        })
      ).unwrap();
      rows = Array.isArray(response) ? response : [];
      setDropdownRows(rows);
    } catch (error) {
      console.error('Unable to load assembler dropdown values:', error);
      setNotice({
        variant: 'danger',
        message: error?.message || 'Unable to load assembler dropdown values.',
      });
    } finally {
      setIsLoading(false);
    }
    return rows;
  }, [dispatch, dropdownValuesUrl]);

  useEffect(() => {
    refreshDropdownValues();
  }, [refreshDropdownValues]);

  const validateForm = (values, currentId = null) => {
    const firstName = normalizeDisplayText(values.firstName);
    const lastName = normalizeDisplayText(values.lastName);
    const displayText = buildDisplayText({ firstName, lastName });

    if (!firstName) return 'First name is required.';
    if (displayText.length > 60) return 'Assembler name must be 60 characters or fewer.';
    if (/[|,;]/.test(displayText)) return 'Assembler name cannot include commas, semicolons, or pipes.';
    if (!/^[A-Za-z0-9][A-Za-z0-9 .'-]*$/.test(displayText)) {
      return 'Assembler name can only use letters, numbers, spaces, periods, apostrophes, and hyphens.';
    }

    const duplicate = assemblerOptions.some((option) => {
      if (String(getOptionId(option)) === String(currentId)) return false;
      return getAssemblerOptionLabel(option).toLowerCase() === displayText.toLowerCase();
    });
    if (duplicate) return `"${displayText}" already exists in the assembler dropdown.`;

    return '';
  };

  const buildPayload = (values, id = null, sortOrder = 1) => {
    const displayText = buildDisplayText(values);
    return {
      ...(id !== null && id !== undefined ? { ID: id } : {}),
      DropdownType: ASSEMBLER_DROPDOWN_TYPE,
      Value: generateValueFromDisplayText(displayText),
      DisplayText: displayText,
      SortOrder: sortOrder,
      IsActive: Boolean(values.isActive),
    };
  };

  const buildPayloadFromOption = (option, sortOrder) => {
    const displayText = getAssemblerOptionLabel(option);
    return {
      ID: getOptionId(option),
      DropdownType: ASSEMBLER_DROPDOWN_TYPE,
      Value: generateValueFromDisplayText(displayText),
      DisplayText: displayText,
      SortOrder: sortOrder,
      IsActive: getOptionBoolean(option?.IsActive ?? option?.isActive),
    };
  };

  const refreshSharedDropdownCache = () => {
    dispatch(portalApi.util.invalidateTags([
      { type: 'DropdownValues', id: 'LIST' },
      'CriticalData',
    ]));
  };

  const rebalanceAlphabeticalSortOrders = async (rows) => {
    const sortedAssemblerRows = (Array.isArray(rows) ? rows : [])
      .filter(isAssemblerDropdownOption)
      .sort((left, right) => (
        getAssemblerOptionLabel(left).localeCompare(getAssemblerOptionLabel(right), undefined, {
          sensitivity: 'base',
        })
      ));

    await Promise.all(sortedAssemblerRows.map((option, index) => {
      const nextSortOrder = index + 1;
      if (getAssemblerOptionSortOrder(option) === nextSortOrder) return Promise.resolve();
      return dispatch(
        portalApi.endpoints.updateDropdownValue.initiate({
          url: dropdownValuesUrl,
          data: buildPayloadFromOption(option, nextSortOrder),
        })
      ).unwrap();
    }));
  };

  const handleCreate = async (event) => {
    event.preventDefault();
    if (!canEdit) {
      setNotice({ variant: 'danger', message: 'You do not have permission to edit assembler dropdown values.' });
      return;
    }
    const validationError = validateForm(formData);
    if (validationError) {
      setNotice({ variant: 'danger', message: validationError });
      return;
    }

    setIsSaving(true);
    setNotice(null);
    try {
      await dispatch(
        portalApi.endpoints.createDropdownValue.initiate({
          url: dropdownValuesUrl,
          data: buildPayload(formData, null, assemblerOptions.length + 1),
        })
      ).unwrap();
      const refreshedRows = await refreshDropdownValues();
      await rebalanceAlphabeticalSortOrders(refreshedRows);
      refreshSharedDropdownCache();
      await refreshDropdownValues();
      setFormData(EMPTY_FORM);
      setNotice({ variant: 'success', message: 'Assembler option added.' });
    } catch (error) {
      console.error('Unable to add assembler option:', error);
      setNotice({ variant: 'danger', message: error?.message || 'Unable to add assembler option.' });
    } finally {
      setIsSaving(false);
    }
  };

  const startEditing = (option) => {
    if (!canEdit) return;
    setEditingId(getOptionId(option));
    setEditData({
      ...splitDisplayText(getAssemblerOptionLabel(option)),
      isActive: getOptionBoolean(option?.IsActive ?? option?.isActive),
    });
    setNotice(null);
  };

  const handleUpdate = async () => {
    if (!canEdit) {
      setNotice({ variant: 'danger', message: 'You do not have permission to edit assembler dropdown values.' });
      return;
    }
    const validationError = validateForm(editData, editingId);
    if (validationError) {
      setNotice({ variant: 'danger', message: validationError });
      return;
    }

    setIsSaving(true);
    setNotice(null);
    try {
      const currentOption = assemblerOptions.find((option) => (
        String(getOptionId(option)) === String(editingId)
      ));
      const currentSortOrder = getAssemblerOptionSortOrder(currentOption);
      await dispatch(
        portalApi.endpoints.updateDropdownValue.initiate({
          url: dropdownValuesUrl,
          data: buildPayload(
            editData,
            editingId,
            currentSortOrder === Number.MAX_SAFE_INTEGER ? assemblerOptions.length : currentSortOrder
          ),
        })
      ).unwrap();
      const refreshedRows = await refreshDropdownValues();
      await rebalanceAlphabeticalSortOrders(refreshedRows);
      refreshSharedDropdownCache();
      await refreshDropdownValues();
      setEditingId(null);
      setEditData(EMPTY_FORM);
      setNotice({ variant: 'success', message: 'Assembler option updated.' });
    } catch (error) {
      console.error('Unable to update assembler option:', error);
      setNotice({ variant: 'danger', message: error?.message || 'Unable to update assembler option.' });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!canEdit) {
      setNotice({ variant: 'danger', message: 'You do not have permission to edit assembler dropdown values.' });
      return;
    }
    if (!deleteTarget) return;
    setIsSaving(true);
    setNotice(null);
    try {
      await dispatch(
        portalApi.endpoints.deleteDropdownValue.initiate({
          url: dropdownValuesUrl,
          id: getOptionId(deleteTarget),
        })
      ).unwrap();
      const refreshedRows = await refreshDropdownValues();
      await rebalanceAlphabeticalSortOrders(refreshedRows);
      refreshSharedDropdownCache();
      await refreshDropdownValues();
      setDeleteTarget(null);
      setNotice({ variant: 'success', message: 'Assembler option deleted.' });
    } catch (error) {
      console.error('Unable to delete assembler option:', error);
      setNotice({ variant: 'danger', message: error?.message || 'Unable to delete assembler option.' });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="assembly-assembler-manager">
      <div className="assembly-manager-header">
        <div>
          <h4>Assembler Dropdown</h4>
          <p className="text-muted mb-0">
            Manage the names available in the Assembly Schedule assembler selector.
          </p>
        </div>
        <Button variant="outline-primary" onClick={refreshDropdownValues} disabled={isLoading || isSaving}>
          <i className={`bi bi-arrow-repeat me-2 ${isLoading ? 'assembly-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {notice && <Alert variant={notice.variant}>{notice.message}</Alert>}
      {!canEdit && (
        <Alert variant="info">
          You have read-only access to the assembler dropdown.
        </Alert>
      )}

      {canEdit && (
        <Form onSubmit={handleCreate} className="assembly-manager-form">
          <Form.Group>
            <Form.Label>First name</Form.Label>
            <Form.Control
              value={formData.firstName}
              onChange={(event) => setFormData((current) => ({
                ...current,
                firstName: event.target.value,
              }))}
              placeholder="First"
              required
              disabled={isSaving}
              maxLength={30}
            />
          </Form.Group>
          <Form.Group>
            <Form.Label>Last name</Form.Label>
            <Form.Control
              value={formData.lastName}
              onChange={(event) => setFormData((current) => ({
                ...current,
                lastName: event.target.value,
              }))}
              placeholder="Last"
              disabled={isSaving}
              maxLength={30}
            />
          </Form.Group>
          <Form.Group className="assembly-manager-active">
            <Form.Check
              type="checkbox"
              label="Active"
              checked={formData.isActive}
              onChange={(event) => setFormData((current) => ({
                ...current,
                isActive: event.target.checked,
              }))}
              disabled={isSaving}
            />
          </Form.Group>
          <Button type="submit" variant="primary" disabled={isSaving}>
            Add Assembler
          </Button>
        </Form>
      )}

      <div className="assembly-manager-table-wrapper">
        {isLoading ? (
          <div className="assembly-loading">
            <Spinner animation="border" variant="primary" />
            <p>Loading assembler options...</p>
          </div>
        ) : (
          <Table bordered hover responsive size="sm" className="assembly-manager-table">
            <thead>
              <tr>
                <th>Assembler</th>
                <th>Value</th>
                <th>Active</th>
                {canEdit && <th className="assembly-manager-actions">Actions</th>}
              </tr>
            </thead>
            <tbody>
              {assemblerOptions.map((option) => {
                const optionId = getOptionId(option);
                const isEditing = String(editingId) === String(optionId);
                return (
                  <tr key={optionId}>
                    {isEditing ? (
                      <>
                        <td>
                          <div className="assembly-manager-name-edit">
                            <Form.Control
                              value={editData.firstName}
                              onChange={(event) => setEditData((current) => ({
                                ...current,
                                firstName: event.target.value,
                              }))}
                              disabled={isSaving}
                              maxLength={30}
                              aria-label="First name"
                            />
                            <Form.Control
                              value={editData.lastName}
                              onChange={(event) => setEditData((current) => ({
                                ...current,
                                lastName: event.target.value,
                              }))}
                              disabled={isSaving}
                              maxLength={30}
                              aria-label="Last name"
                            />
                          </div>
                        </td>
                        <td>{generateValueFromDisplayText(buildDisplayText(editData))}</td>
                        <td>
                          <Form.Check
                            type="checkbox"
                            label="Active"
                            checked={editData.isActive}
                            onChange={(event) => setEditData((current) => ({
                              ...current,
                              isActive: event.target.checked,
                            }))}
                            disabled={isSaving}
                          />
                        </td>
                        {canEdit && (
                          <td className="assembly-manager-actions">
                            <Button variant="success" size="sm" onClick={handleUpdate} disabled={isSaving}>
                              Save
                            </Button>
                            <Button
                              variant="outline-secondary"
                              size="sm"
                              onClick={() => {
                                setEditingId(null);
                                setEditData(EMPTY_FORM);
                              }}
                              disabled={isSaving}
                            >
                              Cancel
                            </Button>
                          </td>
                        )}
                      </>
                    ) : (
                      <>
                        <td>{getAssemblerOptionLabel(option)}</td>
                        <td>{option?.Value ?? option?.value}</td>
                        <td>{String(getOptionBoolean(option?.IsActive ?? option?.isActive))}</td>
                        {canEdit && (
                          <td className="assembly-manager-actions">
                            <Button variant="outline-primary" size="sm" onClick={() => startEditing(option)}>
                              <i className="bi bi-pencil" />
                            </Button>
                            <Button variant="outline-danger" size="sm" onClick={() => setDeleteTarget(option)}>
                              <i className="bi bi-trash" />
                            </Button>
                          </td>
                        )}
                      </>
                    )}
                  </tr>
                );
              })}
              {assemblerOptions.length === 0 && (
                <tr>
                  <td colSpan={canEdit ? 4 : 3} className="text-center text-muted py-4">
                    No assembler options found.
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        )}
      </div>

      <Modal show={Boolean(deleteTarget)} onHide={() => setDeleteTarget(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Delete Assembler</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Delete "{getAssemblerOptionLabel(deleteTarget)}" from the assembler dropdown?
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setDeleteTarget(null)} disabled={isSaving}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDelete} disabled={isSaving}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default AssemblyAssemblerManager;
